/**
 * ============================================================
 *  Mock 数据层（localStorage 持久化）
 * ============================================================
 *
 *  当前为纯前端模式，使用 localStorage 模拟后端存储。
 *
 *  TODO: 对接真实后端时，可按以下步骤清理：
 *    1. 将 api/config.ts 中的 USE_MOCK 改为 false
 *    2. 配置 apiConfig.baseURL 为后端地址
 *    3. 确认 API_ENDPOINTS 中的路径与后端一致
 *    4. 删除本文件（mock-orders.ts）
 *
 * ============================================================
 */

import type {
  OrderRecord,
  Platform,
  ShippingStatus,
  WarehouseStatus,
  OrderPlacer,
} from '@client/src/types/order';
import { getOrderPlacers, getPlatformCountries, getAllPlatforms } from '@client/src/config/env';

const STORAGE_KEY = 'order_management_mock_orders_v1';

function genId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const PLACEHOLDER_IMG =
  'https://picsum.photos/seed/placeholder-order/400/400';

function buildMockOrders(): OrderRecord[] {
  const customerNames = [
    '张三',
    '李四',
    'Emma Johnson',
    '王五',
    'Michael Brown',
    'Olivia Davis',
    'Chen Wei',
    '赵六',
    'James Wilson',
    '钱七',
  ];
  const records: OrderRecord[] = [];
  let index = 0;

  for (const platform of getAllPlatforms()) {
    const countries = getPlatformCountries(platform);
    const placers = getOrderPlacers(platform);
    for (let i = 0; i < 5; i += 1) {
      const shippingStatus: ShippingStatus = i % 2 === 0 ? 'shipped' : 'unshipped';
      const warehouseStatus: WarehouseStatus =
        i % 3 === 0 ? 'arrived' : 'not_arrived';
      const createdAtTs = Date.now() - index * 3600 * 1000 * 6;
      records.push({
        id: genId(),
        platform,
        country: countries[i % countries.length],
        shippingStatus,
        trackingNumber:
          shippingStatus === 'shipped'
            ? `${platform.slice(0, 3).toUpperCase()}${1000000 + index * 97}`
            : null,
        warehouseStatus,
        isRefunded: i % 4 === 0,
        imageUrl: PLACEHOLDER_IMG,
        customerName: customerNames[index % customerNames.length],
        orderPlacer: placers[index % placers.length],
        createdAt: new Date(createdAtTs).toISOString(),
        orderDate: new Date(createdAtTs).toISOString().split('T')[0],
      });
      index += 1;
    }
  }

  return records;
}

class OrderMockStore {
  private orders: OrderRecord[] = [];

  constructor() {
    this.load();
  }

  private load(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        this.orders = JSON.parse(raw);
      } else {
        this.orders = buildMockOrders();
        this.persist();
      }
    } catch {
      this.orders = buildMockOrders();
    }
  }

  private persist(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.orders));
    } catch {
      // ignore storage errors
    }
  }

  reset(): void {
    this.orders = buildMockOrders();
    this.persist();
  }

  list(params: {
    platform: Platform;
    page: number;
    pageSize: number;
    trackingNumber?: string;
    shippingStatus?: ShippingStatus;
    isRefunded?: boolean;
    warehouseStatus?: WarehouseStatus;
    country?: string;
    customerName?: string;
    orderPlacer?: OrderPlacer;
    orderDateMonth?: string;
  }): { items: OrderRecord[]; total: number; page: number; pageSize: number } {
    let filtered = this.orders.filter(
      (order: OrderRecord) => order.platform === params.platform,
    );

    if (params.trackingNumber) {
      const kw = params.trackingNumber.toLowerCase();
      filtered = filtered.filter(
        (o: OrderRecord) =>
          o.trackingNumber?.toLowerCase().includes(kw),
      );
    }
    if (params.shippingStatus) {
      filtered = filtered.filter(
        (o: OrderRecord) => o.shippingStatus === params.shippingStatus,
      );
    }
    if (params.isRefunded !== undefined) {
      filtered = filtered.filter(
        (o: OrderRecord) => o.isRefunded === params.isRefunded,
      );
    }
    if (params.warehouseStatus) {
      filtered = filtered.filter(
        (o: OrderRecord) => o.warehouseStatus === params.warehouseStatus,
      );
    }
    if (params.country) {
      filtered = filtered.filter(
        (o: OrderRecord) => o.country === params.country,
      );
    }
    if (params.customerName) {
      const kw = params.customerName.toLowerCase();
      filtered = filtered.filter(
        (o: OrderRecord) =>
          o.customerName?.toLowerCase().includes(kw),
      );
    }
    if (params.orderPlacer) {
      filtered = filtered.filter(
        (o: OrderRecord) => o.orderPlacer === params.orderPlacer,
      );
    }
    if (params.orderDateMonth) {
      filtered = filtered.filter(
        (o: OrderRecord) => o.orderDate && o.orderDate.startsWith(params.orderDateMonth),
      );
    }

    filtered.sort((a: OrderRecord, b: OrderRecord) =>
      b.createdAt.localeCompare(a.createdAt),
    );

    const total = filtered.length;
    const start = (params.page - 1) * params.pageSize;
    const items = filtered.slice(start, start + params.pageSize);

    return {
      items,
      total,
      page: params.page,
      pageSize: params.pageSize,
    };
  }

  getById(id: string): OrderRecord | null {
    const found = this.orders.find((o: OrderRecord) => o.id === id);
    return found ?? null;
  }

  create(data: {
    platform: Platform;
    country: string;
    shippingStatus: ShippingStatus;
    trackingNumber?: string;
    warehouseStatus: WarehouseStatus;
    isRefunded: boolean;
    imageUrl: string;
    customerName?: string;
    orderPlacer?: OrderPlacer;
    orderDate?: string;
  }): { id: string; success: boolean } {
    const newOrder: OrderRecord = {
      id: genId(),
      platform: data.platform,
      country: data.country,
      shippingStatus: data.shippingStatus,
      trackingNumber: data.trackingNumber ?? null,
      warehouseStatus: data.warehouseStatus,
      isRefunded: data.isRefunded,
      imageUrl: data.imageUrl || PLACEHOLDER_IMG,
      customerName: data.customerName ?? null,
      orderPlacer: data.orderPlacer ?? null,
      createdAt: new Date().toISOString(),
      orderDate: data.orderDate || new Date().toISOString().split('T')[0],
    };
    this.orders.unshift(newOrder);
    this.persist();
    return { id: newOrder.id, success: true };
  }

  update(
    id: string,
    data: {
      country?: string;
      shippingStatus?: ShippingStatus;
      trackingNumber?: string | null;
      warehouseStatus?: WarehouseStatus;
      isRefunded?: boolean;
      imageUrl?: string;
      customerName?: string | null;
      orderPlacer?: OrderPlacer | null;
      orderDate?: string;
    },
  ): { id: string; success: boolean } {
    const idx = this.orders.findIndex((o: OrderRecord) => o.id === id);
    if (idx === -1) {
      throw new Error('订单不存在');
    }
    const current = this.orders[idx];
    const updated: OrderRecord = { ...current };
    if (data.country !== undefined) updated.country = data.country;
    if (data.shippingStatus !== undefined)
      updated.shippingStatus = data.shippingStatus;
    if (data.warehouseStatus !== undefined)
      updated.warehouseStatus = data.warehouseStatus;
    if (data.isRefunded !== undefined) updated.isRefunded = data.isRefunded;
    if (data.imageUrl !== undefined) updated.imageUrl = data.imageUrl;
    if (data.customerName !== undefined)
      updated.customerName = data.customerName ?? null;
    if (data.orderPlacer !== undefined)
      updated.orderPlacer = data.orderPlacer ?? null;
    if (data.orderDate !== undefined) updated.orderDate = data.orderDate;

    if (data.shippingStatus === 'shipped') {
      updated.trackingNumber = data.trackingNumber || updated.trackingNumber;
    } else if (data.shippingStatus === 'unshipped') {
      updated.trackingNumber = null;
    } else if (data.trackingNumber !== undefined) {
      updated.trackingNumber = data.trackingNumber || null;
    }

    this.orders[idx] = updated;
    this.persist();
    return { id, success: true };
  }

  remove(id: string): { success: boolean } {
    const idx = this.orders.findIndex((o: OrderRecord) => o.id === id);
    if (idx === -1) {
      throw new Error('订单不存在');
    }
    this.orders.splice(idx, 1);
    this.persist();
    return { success: true };
  }
}

export const orderMockStore = new OrderMockStore();
